import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import { endpoints } from '../../config/api';
import SimpleBlogCard from '../blog/SimpleBlogCard';

import './RecentBlogs.css';

function RecentBlogs({ currentBlog, limit = 8, variant = 'default' }) {
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentBlogs();
  }, [currentBlog]);

  const fetchRecentBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(endpoints.blogs.list);
      
      if (response.data.success) {
        const blogs = response.data.data.blogPosts || [];
        
        // Process blogs with proper image handling
        const processedBlogs = blogs.map(blog => {
          // Handle undefined or null image - check multiple possible image fields
          let imageUrl = '/images/blog-placeholder.jpg'; // Default image path
          const possibleImageFields = ['heroImage', 'image', 'featuredImage', 'coverImage', 'thumbnail', 'imageUrl', 'img'];
          
          for (const field of possibleImageFields) {
            if (blog[field]) {
              if (typeof blog[field] === 'string' && blog[field].trim() !== '') {
                imageUrl = blog[field];
                break;
              } else if (typeof blog[field] === 'object' && blog[field] !== null) {
                // Handle case where image might be an object with url property
                if (blog[field].url && typeof blog[field].url === 'string') {
                  imageUrl = blog[field].url;
                  break;
                }
              }
            }
          }

          return {
            ...blog,
            heroImage: imageUrl,
            publicationDate: blog.publicationDate || blog.createdAt || blog.date
          };
        });
        
        // Filter out current blog if provided
        const filteredBlogs = currentBlog 
          ? processedBlogs.filter(blog => blog._id !== currentBlog._id)
          : processedBlogs;

        // Sort blogs by publication date in descending order (newest first)
        const sortedBlogs = filteredBlogs.sort((a, b) => {
          const dateA = new Date(a.publicationDate);
          const dateB = new Date(b.publicationDate);
          return dateB - dateA;
        });

        setRecentBlogs(sortedBlogs.slice(0, limit));
      } else {
        setError('Failed to load recent blogs');
        setRecentBlogs([]);
      }
    } catch (error) {
      console.error('Error fetching recent blogs:', error);
      setError('Failed to load recent blogs. Please try again later.');
      setRecentBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Sidebar variant
  if (variant === 'sidebar') {
    if (loading) {
      return (
        <div className="recent-blogs-sidebar">
          <div className="blogs-list-sidebar">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="blog-item-skeleton-sidebar">
                <div className="skeleton-image-sidebar" />
                <div className="skeleton-content-sidebar">
                  <div className="skeleton-title-sidebar" />
                  <div className="skeleton-meta-sidebar" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="recent-blogs-sidebar">
          <div className="error-message-sidebar">
            {error}
          </div>
        </div>
      );
    }

    return (
      <div className="recent-blogs-sidebar">
        <div className="blogs-list-sidebar">
          {recentBlogs.length > 0 ? (
            recentBlogs.map(blog => (
              <Link key={blog._id} to={`/blog/${blog.slug}`} className="blog-item-sidebar">
                <div className="blog-image-sidebar">
                  <img
                    src={blog.heroImage || '/images/blog-placeholder.jpg'}
                    alt={blog.title}
                    className="blog-thumbnail-sidebar"
                    onError={(e) => {
                      e.target.src = '/images/blog-placeholder.jpg';
                    }}
                  />
                </div>
                <div className="blog-info-sidebar">
                  <h4 className="blog-title-sidebar">{blog.title}</h4>
                  <p className="blog-date-sidebar">
                    {new Date(blog.publicationDate || blog.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))
          ) : (
            <div className="no-blogs-sidebar">
              <p>No blog posts available.</p>
            </div>
          )}
        </div>
        <div className="view-all-sidebar">
          <Link to="/blogs" className="view-all-link-sidebar">
            View All Posts →
          </Link>
        </div>
      </div>
    );
  }

  // Default variant
  if (loading) {
    return (
      <section className="recent-blogs">
        <div className="container">
          <div className="section-header">
            <h2>Latest Blog Posts</h2>
            <p>Stay updated with industry insights and career tips</p>
          </div>
          <div className="blogs-grid">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="blog-card-skeleton">
                <div className="skeleton-image" />
                <div className="skeleton-content">
                  <div className="skeleton-title" />
                  <div className="skeleton-text" />
                  <div className="skeleton-meta" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="recent-blogs">
        <div className="container">
          <div className="section-header">
            <h2>Latest Blog Posts</h2>
            <p>Stay updated with industry insights and career tips</p>
          </div>
          <div className="error-message">
            {error}
            <button onClick={fetchRecentBlogs} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="recent-blogs">
      <div className="container">
        <div className="section-header">
          <h2>Latest Blog Posts</h2>
          <p>Stay updated with industry insights and career tips</p>
        </div>
        
        <div className="blogs-grid">
          {recentBlogs.length > 0 ? (
            recentBlogs.map(blog => (
              <SimpleBlogCard key={blog._id} blog={blog} />
            ))
          ) : (
            <div className="no-blogs">
              <p>No blog posts available at the moment.</p>
            </div>
          )}
        </div>

        <div className="view-all-container">
          <Link to="/blogs" className="view-all-blogs">
            View All Blog Posts
            <FaArrowRight />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default RecentBlogs; 